import React, { useState, useEffect } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
    Tabs, TabList, Tab, TabPanels, TabPanel,
    SimpleGrid,
    FormControl, FormLabel, Input, Textarea,
    Flex, Box, Text, VStack,
    Checkbox,
    Badge,
    Button,
    Divider,
    Spinner,
    Link,
    Image,
    Stat, StatLabel, StatNumber, StatHelpText,
    Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, AspectRatio,
    Tag,
    HStack,
    useMediaQuery,
} from '@chakra-ui/react';
import { FaExternalLinkAlt, FaWhatsapp } from "react-icons/fa";
import ProdutoImagem from "../ProdutoImagem";
import ProdutoImagensTab from "../ProdutoImagensTab";
import { getInfoAnuncioMLModal } from "../../services/api";

// --- FUNÇÕES E COMPONENTES AUXILIARES ---

// Duração do bloqueio temporário, em dias. Espelha Produto::DIAS_BLOQUEIO_TEMPORARIO
// no backend (app/Models/Produto.php). Ao mudar aqui, mudar lá também.
const DIAS_BLOQUEIO_TEMPORARIO = 15;

// Espelha BloqueioProdutoService::ROTULOS_CAUSA no backend.
const ROTULOS_CAUSA_BLOQUEIO = {
    furo_fornecedor: 'Furo de fornecedor',
    conferencia_divergente: 'Conferência de despacho',
    devolucao_divergente: 'Divergência na devolução',
    lista_massa: 'Bloqueio em massa',
    manual: 'Bloqueio manual',
    planilha: 'Planilha do fornecedor',
    script_pontual: 'Script pontual',
    fornecedor_descontinuado: 'Fornecedor descontinuado',
    sem_dimensao: 'Sem dimensão (aguardando medição)',
    desconhecido: 'Sem causa registrada',
};

/** Data em que o bloqueio temporário termina, ou null se não houver bloqueio. */
const calcularFimDoBloqueio = (bloqueioTemporario) => {
    if (!bloqueioTemporario) return null;
    const fim = new Date(String(bloqueioTemporario).replace(' ', 'T'));
    if (isNaN(fim.getTime())) return null;
    fim.setDate(fim.getDate() + DIAS_BLOQUEIO_TEMPORARIO);
    return fim;
};

const formatarParaBRL = (valor) => {
    const numero = Number(valor);
    if (isNaN(numero)) return "R$ 0,00";
    return numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const FormField = ({ label, value }) => (
    <FormControl id={label.toLowerCase().replace(/\s/g, '-')}>
        <FormLabel fontSize="sm" fontWeight="medium" color="gray.600" mb={1}>{label}</FormLabel>
        <Input isReadOnly value={value ?? 'N/A'} bg="white" boxShadow="sm" />
    </FormControl>
);

const Section = ({ title, children, ...props }) => (
    <VStack spacing={4} align="stretch" w="100%" {...props}>
        <Flex align="center">
            <Text fontSize="lg" fontWeight="semibold" color="gray.800" whiteSpace="nowrap">{title}</Text>
            <Divider ml={4} />
        </Flex>
        {children}
    </VStack>
);


const AnuncioMLPanel = ({ produto_id, conta }) => {
    const [anuncio, setAnuncio] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [imagemPrincipal, setImagemPrincipal] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const data = await getInfoAnuncioMLModal(produto_id, conta);
                setAnuncio(data);
                if (data && data.pictures && data.pictures.length > 0) {
                    setImagemPrincipal(data.pictures[0].secure_url);
                }
            } catch (error) {
                console.error("Erro ao buscar dados do anúncio ML:", error);
                setAnuncio(null);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [produto_id, conta]);

    if (isLoading) {
        return <Flex justify="center" align="center" h="200px"><Spinner size="xl" color="orange.500" /></Flex>;
    }

    if (!anuncio) {
        return (
            <Flex direction="column" justify="center" align="center" h="200px" bg="gray.50" borderRadius="md">
                <Text fontSize="lg" fontWeight="bold" color="gray.600">Anúncio não encontrado</Text>
                <Text color="gray.500">Não foi possível localizar um anúncio para este produto nesta conta.</Text>
            </Flex>
        );
    }

    const getAttributeValue = (id) => anuncio.attributes?.find(attr => attr.id === id)?.value_name || 'N/A';

    const listingTypes = {
        gold_special: 'Ouro Especial',
        gold_pro: 'Ouro Premium',
        free: 'Grátis',
    };

    return (
        <Flex direction={{ base: "column", lg: "row" }} gap={{ base: 6, md: 8 }}>
            {/* Coluna Esquerda: Imagens e Ações */}
            <VStack w={{ base: "100%", lg: "350px" }} spacing={4} align="stretch" position={{ lg: "sticky" }} top={0}>
                <Box>
                    {/* 3. IMAGEM PRINCIPAL DENTRO DE UM ASPECTRATIO - REMOVIDO ratio={1} */}
                    <AspectRatio w="100%" borderRadius="lg" boxShadow="lg" bg="white" overflow="hidden">
                        <Image
                            src={imagemPrincipal}
                            alt="Imagem principal do produto"
                            objectFit="contain"
                        />
                    </AspectRatio>

                    {/* 4. GALERIA DE MINIATURAS INTERATIVAS - MANTIDO ratio={1} */}
                    <SimpleGrid columns={5} spacing={2} mt={2}>
                        {(anuncio.pictures || []).slice(0, 5).map(pic => (
                            <AspectRatio ratio={1} key={pic.id}>
                                <Image
                                    src={pic.secure_url}
                                    alt="Miniatura do produto"
                                    borderRadius="md"
                                    cursor="pointer"
                                    onClick={() => setImagemPrincipal(pic.secure_url)}
                                    border="2px solid"
                                    borderColor={imagemPrincipal === pic.secure_url ? 'orange.500' : 'transparent'}
                                    transition="border-color 0.2s"
                                    objectFit="cover" // Manter cover para preencher o quadrado, ou mudar para "contain" se preferir ver a imagem inteira com espaços
                                />
                            </AspectRatio>
                        ))}
                    </SimpleGrid>
                </Box>
                <Button as={Link} href={anuncio.permalink} isExternal colorScheme="yellow" leftIcon={<Image src="/ml_logo_2.webp" w="80px" />}>
                    Ver Anúncio
                </Button>
            </VStack>

            {/* Coluna Direita: Detalhes do Anúncio */}
            <VStack flex="1" spacing={8} align="stretch">
                <VStack spacing={4} align="stretch" bg="white" p={4} borderRadius="lg" boxShadow="sm">
                    <Text fontSize="2xl" fontWeight="bold" color="gray.800" lineHeight="tight">{anuncio.title}</Text>
                    <Text fontSize="3xl" fontWeight="light" color="gray.900">{formatarParaBRL(anuncio.price)}</Text>
                    <Flex wrap="wrap" gap={2}>
                        <Badge colorScheme={anuncio.listing_type_id === 'gold_special' ? 'yellow' : 'gray'}>
                            {listingTypes[anuncio.listing_type_id] || anuncio.listing_type_id}
                        </Badge>
                        <Badge colorScheme={anuncio.condition === 'new' ? 'green' : 'orange'}>
                            {getAttributeValue('ITEM_CONDITION')}
                        </Badge>
                    </Flex>
                </VStack>

                <Section title="Métricas de Venda">
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                        <Stat><StatLabel>Disponíveis</StatLabel><StatNumber>{anuncio.available_quantity}</StatNumber></Stat>
                        <Stat><StatLabel>Vendidos</StatLabel><StatNumber>{anuncio.sold_quantity}</StatNumber></Stat>
                        <Stat><StatLabel>Status</StatLabel><StatNumber><Badge colorScheme={anuncio.status === 'active' ? 'green' : 'red'} fontSize="md">{anuncio.status}</Badge></StatNumber></Stat>
                        <Stat><StatLabel>Saúde do Anúncio</StatLabel><StatNumber color={anuncio.health > 0.8 ? 'green.500' : 'orange.500'}>{(anuncio.health * 100).toFixed(0)}%</StatNumber></Stat>
                    </SimpleGrid>
                </Section>

                {/* NOVA SEÇÃO: DETALHES DA PUBLICAÇÃO */}
                <Section title="Detalhes da Publicação">
                    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
                        <FormField label="ID do Anúncio" value={anuncio.id} />
                        <FormField label="Marca" value={getAttributeValue('BRAND')} />
                        <FormField label="Garantia" value={anuncio.warranty} />
                        <FormField label="Data de Criação" value={new Date(anuncio.date_created).toLocaleString('pt-BR')} />
                        <FormField label="Última Atualização" value={new Date(anuncio.last_updated).toLocaleString('pt-BR')} />
                        <FormControl>
                            <FormLabel fontSize="sm" fontWeight="medium" color="gray.600" mb={1}>Canais de Venda</FormLabel>
                            <HStack>
                                {anuncio.channels.map(channel => <Tag key={channel} colorScheme="blue">{channel}</Tag>)}
                            </HStack>
                        </FormControl>
                    </SimpleGrid>
                </Section>

                {/* NOVA SEÇÃO: LOGÍSTICA E ENVIO */}
                <Section title="Logística e Envio">
                    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
                        <FormField label="Modo de Envio" value={anuncio.shipping?.mode} />
                        <FormField label="Tipo Logístico" value={anuncio.shipping?.logistic_type} />
                        <FormField label="Frete Grátis" value={anuncio.shipping?.free_shipping ? 'Sim' : 'Não'} />
                    </SimpleGrid>
                </Section>

                {/* NOVO BLOCO: VISUALIZADOR DE JSON */}
                <Accordion allowToggle width="100%" pt={6}>
                    <AccordionItem>
                        <h2>
                            <AccordionButton _expanded={{ bg: 'orange.500', color: 'white' }}>
                                <Box flex="1" textAlign="left" fontWeight="bold">
                                    Visualizar Dados Brutos (JSON)
                                </Box>
                                <AccordionIcon />
                            </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4} bg="gray.800" color="gray.100" maxH="400px" overflowY="auto">
                            <pre style={{ 'white-space': 'pre-wrap', 'word-wrap': 'break-word' }}>
                                {JSON.stringify(anuncio, null, 2)}
                            </pre>
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            </VStack>
        </Flex>
    );
};

// --- COMPONENTE PRINCIPAL DO MODAL ---
const ProdutoDetalhesModal = ({ isOpen, onClose, produto }) => {

    const [isMobile] = useMediaQuery("(max-width: 768px)");

    if (!produto) return null;


    return (
        <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside" isCentered>
            <ModalOverlay bg="blackAlpha.600" />
            <ModalContent maxH="90vh" bg="gray.100">
                <ModalHeader><Text>Detalhes do Produto</Text><ModalCloseButton /></ModalHeader>
                <ModalBody>
                    <Tabs isLazy variant="enclosed-colored" colorScheme="orange">
                        <TabList overflowX="auto" overflowY="hidden" flexWrap={{ base: "nowrap", md: "wrap" }}>
                            <Tab whiteSpace="nowrap">Produto TAGA</Tab>
                            <Tab whiteSpace="nowrap">Imagens</Tab>
                            <Tab whiteSpace="nowrap">Anúncio ML (1)</Tab>
                            <Tab whiteSpace="nowrap">Anúncio ML (2)</Tab>
                            <Tab whiteSpace="nowrap">Anúncio ML (3)</Tab>
                        </TabList>
                        <TabPanels>
                            {/* ABA 1: Produto TAGA */}
                            <TabPanel px={{ base: 2, md: 4 }} py={6}>

                                {!isMobile ?
                                    (
                                        <Flex direction={{ base: "column", lg: "row" }} gap={{ base: 6, md: 8 }}>

                                            {/* COLUNA ESQUERDA (IMAGEM E INFO PRIMÁRIA) */}
                                            <VStack w={{ base: "100%", lg: "350px" }} spacing={6} align="stretch" position={{ lg: "sticky" }} top={0}>
                                                <ProdutoImagem produto={produto} origem={""} borderRadius="lg" boxShadow="lg" />

                                                <Flex mt={4} justifyContent="space-between" alignItems="center">
                                                    <Text fontWeight="bold">Status:</Text>
                                                    <Badge px={3} py={1} borderRadius="full" fontSize="sm" colorScheme={produto.bloqueado === 1 ? "red" : produto.bloqueado === 2 ? "yellow" : "green"}>
                                                        {produto.bloqueado === 1 ? "Bloqueado" : produto.bloqueado === 2 ? "Bloqueio Temporário" : "Ativo"}
                                                    </Badge>
                                                </Flex>
                                                {produto.bloqueado === 2 && (() => {
                                                    const dataExpiracao = calcularFimDoBloqueio(produto.bloqueio_temporario);
                                                    if (!dataExpiracao) return null;

                                                    return (
                                                        <Text fontSize="xs" color="gray.600" mt={-4}>
                                                            Até: {dataExpiracao.toLocaleString('pt-BR')}
                                                        </Text>
                                                    );
                                                })()}

                                                {produto.bloqueado !== 0 && (
                                                    <Box mt={-2} bg="red.50" borderWidth="1px" borderColor="red.100" borderRadius="md" p={3}>
                                                        <Text fontSize="xs" color="red.600" fontWeight="bold" textTransform="uppercase" letterSpacing="wide" mb={1}>
                                                            Por que está bloqueado
                                                        </Text>
                                                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                                            {ROTULOS_CAUSA_BLOQUEIO[produto.bloqueio_causa] || 'Sem causa registrada'}
                                                        </Text>
                                                        {produto.bloqueio_motivo_texto && (
                                                            <Text fontSize="sm" color="gray.700" mt={1}>{produto.bloqueio_motivo_texto}</Text>
                                                        )}
                                                        {produto.bloqueio_origem && (
                                                            <Text fontSize="xs" color="gray.500" mt={1}>Origem: {produto.bloqueio_origem}</Text>
                                                        )}
                                                        {produto.bloqueio_em && (
                                                            <Text fontSize="xs" color="gray.500">
                                                                Bloqueado em {new Date(produto.bloqueio_em).toLocaleDateString('pt-BR')}
                                                                {produto.bloqueio_user ? ` por ${produto.bloqueio_user}` : ''}
                                                            </Text>
                                                        )}
                                                    </Box>
                                                )}

                                                <Section title="Dimensões e Peso">
                                                    <SimpleGrid columns={2} spacing={4}>
                                                        <FormField label="Altura (cm)" value={produto.altura || '0'} />
                                                        <FormField label="Largura (cm)" value={produto.largura || '0'} />
                                                        <FormField label="Comp. (cm)" value={produto.comprimento || '0'} />
                                                        <FormField label="Peso (kg)" value={produto.peso || '0'} />
                                                    </SimpleGrid>
                                                    <FormControl display="flex" alignItems="center" mt={4}>
                                                        <FormLabel mb="0" fontSize="sm" color="gray.700">Dimensões do ML?</FormLabel>
                                                        {/* Só medida de verdade (1, 2 e 3). A provisória da regra BL (4) ainda está na fila de medição. */}
                                                        <Checkbox isReadOnly isChecked={[1, 2, 3].includes(Number(produto.dimensoes_atualizadas))} mr={3} />
                                                    </FormControl>
                                                </Section>

                                                {/* --- BLOCO DE HISTÓRICO MOVIDO PARA CÁ --- */}
                                                <Section title="Histórico">
                                                    <SimpleGrid columns={1} spacing={4}>
                                                        <FormField fontSize="10px" label="Data de Criação" value={new Date(produto.data_criacao || Date.now()).toLocaleString('pt-BR')} />
                                                        <FormField fontSize="10px" label="Última Atualização" value={new Date(produto.data_ultima_atualizacao || Date.now()).toLocaleString('pt-BR')} />
                                                    </SimpleGrid>
                                                </Section>
                                            </VStack>

                                            {/* COLUNA DIREITA (DETALHES E SEÇÕES) */}
                                            <VStack flex="1" spacing={8} align="stretch">
                                                <VStack spacing={4} align="stretch">
                                                    <FormField label="Nome do Produto" value={produto.nome || 'N/A'} />
                                                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} pt={2}>
                                                        <FormField label="Fabricante" value={produto.fabricante_nome || 'N/A'} />
                                                        <FormField label="Fornecedor" value={produto.fornecedor || 'N/A'} />
                                                        <FormField label="Modelo" value={produto.modelo || 'N/A'} />
                                                    </SimpleGrid>
                                                    <FormControl id="aplicacao" pt={2}>
                                                        <FormLabel fontSize="sm" fontWeight="medium" color="gray.600" mb={1}>Aplicação</FormLabel>
                                                        <Textarea isReadOnly value={produto.descricao || 'N/A'} bg="white" boxShadow="sm" rows={10} resize="vertical" />
                                                    </FormControl>
                                                </VStack>

                                                <Section title="Códigos e Identificadores">
                                                    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
                                                        <FormField label="SKU / ID" value={produto.produto_id} />
                                                        <FormField label="Código Global" value={produto.codigo_global} />
                                                        <FormField label="Cód. Fornecedor" value={produto.codigo_fornecedor} />
                                                        <FormField label="Cód. Fabricante" value={produto.codigo_fabricante} />
                                                        <FormField label="NCM" value={produto.ncm || 'N/A'} />
                                                        <FormField label="Código de Barras" value={produto.codigo_barra || 'N/A'} />
                                                    </SimpleGrid>
                                                    <FormControl id="codigos-similares">
                                                        <FormLabel fontSize="sm" fontWeight="medium" color="gray.600" mb={1}>Códigos Similares</FormLabel>
                                                        <Textarea isReadOnly value={produto.codigo_similares || 'N/A'} bg="white" boxShadow="sm" rows={3} resize="vertical" />
                                                    </FormControl>
                                                </Section>

                                                <Section title="Estoque e Valores">
                                                    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
                                                        <FormField label="Estoque" value={`${produto.quantidade || 0} un.`} />
                                                        <FormField label="Estoque TAGA" value={`${produto.quantidade_taga || 0} un.`} />
                                                        <FormField label="Unidades/Emb." value={produto.unidades_embalagem || '1'} />
                                                        <FormField label="Valor de Compra" value={formatarParaBRL(produto.valor_compra)} />
                                                        <FormField label="Valor de Venda" value={formatarParaBRL(produto.valor_venda)} />
                                                        <FormField label="Valor Estoque TAGA" value={formatarParaBRL((produto.quantidade_taga || 0) * (produto.valor_compra || 0))} />
                                                    </SimpleGrid>

                                                    {produto.locais && produto.locais.length > 0 && (
                                                        <Box mt={4}>
                                                            <Text fontSize="md" fontWeight="bold" mb={3}>Localizações de Estoque TAGA</Text>
                                                            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
                                                                {produto.locais.map((local, index) => (
                                                                    <Flex key={index} align="center" justify="space-between" p={2} bg="gray.50" borderRadius="md" boxShadow="xs">
                                                                        <Text fontSize="sm" fontWeight="medium">
                                                                            {local.localizacao}
                                                                        </Text>
                                                                        <Text fontSize="sm" color="gray.700">
                                                                            {local.quantidade} un.
                                                                        </Text>
                                                                    </Flex>
                                                                ))}
                                                            </SimpleGrid>
                                                        </Box>
                                                    )}
                                                </Section>

                                                {/* Bloco de Histórico foi removido daqui */}
                                            </VStack>
                                        </Flex>

                                    )
                                    :
                                    (
                                        <VStack spacing={8} align="stretch">
                                            {/* 1. IMAGEM E STATUS */}
                                            <VStack spacing={4}>
                                                <ProdutoImagem produto={produto} origem={""} borderRadius="lg" boxShadow="lg" w="100%" />
                                                <Flex mt={4} justifyContent="space-between" alignItems="center" w="100%">
                                                    <Text fontWeight="bold">Status:</Text>
                                                    <Badge px={3} py={1} borderRadius="full" fontSize="sm" colorScheme={produto.bloqueado === 1 ? "red" : produto.bloqueado === 2 ? "yellow" : "green"}>
                                                        {produto.bloqueado === 1 ? "Bloqueado" : produto.bloqueado === 2 ? "Bloqueio Temporário" : "Ativo"}
                                                    </Badge>
                                                </Flex>
                                                {produto.bloqueado === 2 && (() => {
                                                    const dataExpiracao = calcularFimDoBloqueio(produto.bloqueio_temporario);
                                                    if (!dataExpiracao) return null;

                                                    return (
                                                        <Text fontSize="xs" color="gray.600" mt={-4}>Até: {dataExpiracao.toLocaleString('pt-BR')}</Text>
                                                    );
                                                })()}
                                            </VStack>

                                            {/* 2. DADOS DO PRODUTO */}
                                            <VStack spacing={4} align="stretch">
                                                <FormField label="Nome do Produto" value={produto.nome || 'N/A'} />
                                                <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4} pt={2}>
                                                    <FormField label="Fabricante" value={produto.fabricante_nome || 'N/A'} />
                                                    <FormField label="Fornecedor" value={produto.fornecedor || 'N/A'} />
                                                    <FormField label="Modelo" value={produto.modelo || 'N/A'} />
                                                </SimpleGrid>
                                                <FormControl id="aplicacao-mobile" pt={2}>
                                                    <FormLabel fontSize="sm" fontWeight="medium" color="gray.600" mb={1}>Aplicação</FormLabel>
                                                    <Textarea isReadOnly value={produto.descricao || 'N/A'} bg="white" boxShadow="sm" rows={10} resize="vertical" />
                                                </FormControl>
                                            </VStack>

                                            {/* 3. CÓDIGOS E IDENTIFICADORES */}
                                            <Section title="Códigos e Identificadores">
                                                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                                                    <FormField label="SKU / ID" value={produto.produto_id} />
                                                    <FormField label="Código Global" value={produto.codigo_global} />
                                                    <FormField label="Código de Barras" value={produto.codigo_barra || 'N/A'} />
                                                    <FormField label="Cód. Fornecedor" value={produto.codigo_fornecedor} />
                                                    <FormField label="Cód. Fabricante" value={produto.codigo_fabricante} />
                                                    <FormField label="NCM" value={produto.ncm || 'N/A'} />
                                                </SimpleGrid>
                                                <FormControl id="codigos-similares-mobile">
                                                    <FormLabel fontSize="sm" fontWeight="medium" color="gray.600" mb={1}>Códigos Similares</FormLabel>
                                                    <Textarea isReadOnly value={produto.codigo_similares || 'N/A'} bg="white" boxShadow="sm" rows={3} resize="vertical" />
                                                </FormControl>
                                            </Section>

                                            {/* 4. ESTOQUE E VALORES */}
                                            <Section title="Estoque e Valores">
                                                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                                                    <FormField label="Estoque" value={`${produto.quantidade || 0} un.`} />
                                                    <FormField label="Estoque TAGA" value={`${produto.quantidade_taga || 0} un.`} />
                                                    <FormField label="Unidades/Emb." value={produto.unidades_embalagem || '1'} />
                                                    <FormField label="Valor de Compra" value={formatarParaBRL(produto.valor_compra)} />
                                                    <FormField label="Valor de Venda" value={formatarParaBRL(produto.valor_venda)} />
                                                    <FormField label="Valor Estoque TAGA" value={formatarParaBRL((produto.quantidade_taga || 0) * (produto.valor_compra || 0))} />
                                                </SimpleGrid>

                                                {produto.locais && produto.locais.length > 0 && (
                                                        <Box mt={4}>
                                                            <Text fontSize="md" fontWeight="bold" mb={3}>Localizações de Estoque TAGA</Text>
                                                            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
                                                                {produto.locais.map((local, index) => (
                                                                    <Flex key={index} align="center" justify="space-between" p={2} bg="gray.50" borderRadius="md" boxShadow="xs">
                                                                        <Text fontSize="sm" fontWeight="medium">
                                                                            {local.localizacao}
                                                                        </Text>
                                                                        <Text fontSize="sm" color="gray.700">
                                                                            {local.quantidade} un.
                                                                        </Text>
                                                                    </Flex>
                                                                ))}
                                                            </SimpleGrid>
                                                        </Box>
                                                    )}
                                            </Section>

                                            {/* 5. DIMENSÕES */}
                                            <Section title="Dimensões e Peso">
                                                <SimpleGrid columns={2} spacing={4}>
                                                    <FormField label="Altura (cm)" value={produto.altura || '0'} />
                                                    <FormField label="Largura (cm)" value={produto.largura || '0'} />
                                                    <FormField label="Comp. (cm)" value={produto.comprimento || '0'} />
                                                    <FormField label="Peso (kg)" value={produto.peso || '0'} />
                                                </SimpleGrid>
                                                <FormControl display="flex" alignItems="center" mt={4}>
                                                    <Checkbox isReadOnly isChecked={produto.dimensoes_ml || false} mr={3} />
                                                    <FormLabel mb="0" fontSize="sm" color="gray.700">Dimensões do ML?</FormLabel>
                                                </FormControl>
                                            </Section>

                                            {/* 6. HISTÓRICO */}
                                            <Section title="Histórico">
                                                <SimpleGrid columns={1} spacing={4}>
                                                    <FormField label="Data de Criação" value={new Date(produto.data_criacao || Date.now()).toLocaleString('pt-BR')} />
                                                    <FormField label="Última Atualização" value={new Date(produto.data_ultima_atualizacao || Date.now()).toLocaleString('pt-BR')} />
                                                </SimpleGrid>
                                            </Section>
                                        </VStack>
                                    )
                                }
                            </TabPanel>

                            <TabPanel px={{ base: 2, md: 4 }} py={4}>
                                <ProdutoImagensTab produtoId={produto.produto_id || produto.id} />
                            </TabPanel>

                            <TabPanel>
                                <AnuncioMLPanel produto_id={produto.produto_id} conta={1} />
                            </TabPanel>

                            <TabPanel>
                                <AnuncioMLPanel produto_id={produto.produto_id} conta={2} />
                            </TabPanel>

                            <TabPanel>
                                <AnuncioMLPanel produto_id={produto.produto_id} conta={3} />
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </ModalBody>
                <ModalFooter><Button onClick={onClose}>Fechar</Button></ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ProdutoDetalhesModal;